
export class ProcessImageResults {
    constructor(readonly semanticUrl: string, readonly lightingUrl: string,
                readonly dataUrl: string | undefined, readonly superpixelsUrl: string | undefined,
                readonly fieldOfView: number, readonly cameraRotation: [number, number, number],
                readonly cameraElevation: number, readonly floorRotation: number) {

    }
}

export interface ImageProcessor {
    processImage(imageId: string): Promise<ProcessImageResults | null>
}

export type GetUploadUrlsRequest = {
    key?: string // When defined, will request sub-urls for existing room
    names: string[] // Sub-names like mask, preview
}

export type GetUploadUrlsResult = {
    key: string // Room id
    uploadUrl?: string // Upload url for roomId's background
    subKey: string // Sub-room id for the room id
    subUploadUrls: { [name: string]: string } // Sub-upload urls for requested names for subRoomId
}

export interface ImageUploader {
    getUploadUrls(request: GetUploadUrlsRequest): Promise<GetUploadUrlsResult | null>
    upload(url: string, content: File | Blob): Promise<boolean>
}

export class CambrianImageProcessor implements ImageProcessor {
    constructor(readonly segmentUrl: string) {

    }

    async processImage(imageId: string): Promise<ProcessImageResults | null> {
        const response = await fetch(`${this.segmentUrl}/${imageId}`);

        if (response.ok) {
            const responseData = await response.json();
            return new ProcessImageResults(responseData.semantic_url, responseData.lighting_url,
                responseData.data_url, responseData.superpixels_url, responseData.fov,
                responseData.camera_rotation, responseData.camera_elevation, responseData.floor_rotation)
        }

        return null
    }
}

export class PresignedUrlImageUploader implements ImageUploader {
    constructor(readonly uploadUrl: string) {
        //console.log(`Using PresignedUrlImageUploader at ${uploadUrl}`)
    }

    async getUploadUrls(req: GetUploadUrlsRequest): Promise<GetUploadUrlsResult | null> {

        let url = `${this.uploadUrl}/?imagenames=${req.names.join(",")}`;
        if (req.key) {
            url += `&key=${req.key}`
        }

        const presignedResponse = await fetch(url);
        if (!presignedResponse.ok) {
            console.warn("Response was invalid");
            return null
        }

        const presignedResponseData = await presignedResponse.json();

        if (presignedResponseData.key) {
            return {
                key: presignedResponseData.key,
                uploadUrl: presignedResponseData.uploadUrl,
                subKey: presignedResponseData.subKey,
                subUploadUrls: presignedResponseData.subUploadUrls
            }
        }

        return null
    }

    async upload(url: string, content: File | Blob): Promise<boolean> {
        // Upload the content to the presigned url
        const uploadResponse = await fetch(url, {
            method: "PUT",
            body: content
        });

        return uploadResponse.ok
    }
}

export class DirectImageUploader implements ImageUploader {
    constructor(readonly uploadUrl:string) {
        console.log(`Using DirectImageUploader at ${uploadUrl}`)
    }

    async getUploadUrls(req: GetUploadUrlsRequest): Promise<GetUploadUrlsResult | null> {
        throw new Error("getUploadUrls not implemented for DirectImageUploader")
    }

    async upload(url: string, content: File | Blob): Promise<boolean> {
        throw new Error("upload not implemented for DirectImageUploader")
    }
}

export type CBServerConfig = {
    uploadUrl:string,
    processingUrl:string,
    projectHostingUrl:string,
    uploadNames:string[]
    orientationImage:string //orientation6.jpg used in orientation calculations
}

// Get upload and segment URLs from env vars (defined in .env.*)

let _config:CBServerConfig|undefined;

// Make sure either direct upload url or presigned upload url are defined, and not both (exclusive or).
export function cbInitialize(config:CBServerConfig) {
    _config = config
}

export function getConfig():CBServerConfig {
    if (!_config) {
        throw Error("react-home-harmony is not initialized, call cbInitialize(config:CBServerConfig)")
    }
    return _config
}